import React, { useState } from "react";
import { Robot, Translate, User, Check } from "@phosphor-icons/react";
import { useAuth } from "../context/AuthContext";
import { useI18n } from "../lib/i18n";

const MODELS = [
  { id: "gpt-5.4", name: "GPT-5.4", provider: "OpenAI" },
  { id: "claude-sonnet-4-6", name: "Claude Sonnet 4.6", provider: "Anthropic" },
];

export default function Settings() {
  const { t, lang, setLang } = useI18n();
  const { user } = useAuth();
  const [model, setModel] = useState(localStorage.getItem("lexadm_model") || "gpt-5.4");

  const pickModel = (id) => {
    setModel(id);
    localStorage.setItem("lexadm_model", id);
  };

  return (
    <div className="max-w-3xl mx-auto px-6 lg:px-10 py-10 lg:py-14">
      <header className="animate-fade-up">
        <p className="label-caps text-gold">LEXADM</p>
        <h1 className="font-display font-bold text-4xl tracking-tight text-navy mt-2">
          {t("set.title")}
        </h1>
      </header>

      {/* AI Model */}
      <section className="mt-8 bg-white border border-slate-200 rounded-lg p-7 animate-fade-up" style={{ animationDelay: "40ms" }}>
        <div className="flex items-center gap-2.5 mb-1">
          <Robot size={20} className="text-navy" />
          <h2 className="text-base font-bold text-navy">{t("set.model")}</h2>
        </div>
        <p className="text-sm text-slate-500 mb-5">{t("set.modelHint")}</p>
        <div className="grid sm:grid-cols-2 gap-3">
          {MODELS.map((m) => {
            const active = model === m.id;
            return (
              <button
                key={m.id}
                onClick={() => pickModel(m.id)}
                data-testid={`model-${m.id}`}
                className={`flex items-center justify-between rounded-md border p-4 text-left transition-all duration-200 ${
                  active ? "border-navy bg-navy/[0.03]" : "border-slate-200 hover:border-slate-300"
                }`}
              >
                <div>
                  <div className="text-sm font-bold text-navy">{m.name}</div>
                  <div className="text-xs text-slate-500">{m.provider}</div>
                </div>
                {active && (
                  <span className="flex items-center justify-center w-5 h-5 rounded-full bg-navy text-white">
                    <Check size={12} weight="bold" />
                  </span>
                )}
              </button>
            );
          })}
        </div>
      </section>

      {/* Language */}
      <section className="mt-5 bg-white border border-slate-200 rounded-lg p-7 animate-fade-up" style={{ animationDelay: "80ms" }}>
        <div className="flex items-center gap-2.5 mb-4">
          <Translate size={20} className="text-navy" />
          <h2 className="text-base font-bold text-navy">{t("set.lang")}</h2>
        </div>
        <div className="inline-flex rounded-md border border-slate-200 p-1">
          {["pt", "en"].map((l) => (
            <button
              key={l}
              onClick={() => setLang(l)}
              data-testid={`lang-${l}`}
              className={`px-5 py-2 rounded text-sm font-semibold transition-colors duration-200 ${
                lang === l ? "bg-navy text-white" : "text-slate-500 hover:text-navy"
              }`}
            >
              {l === "pt" ? "Português" : "English"}
            </button>
          ))}
        </div>
      </section>

      {/* Account */}
      <section className="mt-5 bg-white border border-slate-200 rounded-lg p-7 animate-fade-up" style={{ animationDelay: "120ms" }}>
        <div className="flex items-center gap-2.5 mb-4">
          <User size={20} className="text-navy" />
          <h2 className="text-base font-bold text-navy">{t("set.account")}</h2>
        </div>
        <div className="flex items-center gap-4">
          <span className="flex items-center justify-center w-12 h-12 rounded-full bg-navy text-gold-bright">
            <User size={22} weight="fill" />
          </span>
          <div>
            <div className="text-sm font-semibold text-navy">{user?.name}</div>
            <div className="text-sm text-slate-500">{user?.email}</div>
          </div>
        </div>
      </section>
    </div>
  );
}
